import { BarRow, ColumnChart, TrendChart } from '@/features/reports/components/charts';
import { MetricCard } from '@/features/reports/components/MetricCard';
import { ReportCard } from '@/features/reports/components/ReportCard';
import {
  useDailySales,
  usePeakHours,
  usePeriodComparison,
} from '@/features/reports/hooks/useReports';
import { DAY_PART_KEY } from '@/features/reports/lib/dayParts';
import { pctChange } from '@/features/reports/lib/delta';
import { formatRangeLabel, type DateRange } from '@/features/reports/lib/range';
import { useT } from '@/i18n';
import { formatMoney } from '@/utils/money';
import type { ComparisonBasis } from '@restaurant/api-client';
import EmptyState from '@/components/EmptyState';
import { Block, Preloader } from 'framework7-react';

/** Money in: what came in over the period and how that compares. */
export function SalesSection({
  range,
  basis,
  locale,
  dineInEnabled,
}: {
  range: DateRange;
  basis: ComparisonBasis;
  locale: string;
  dineInEnabled: boolean;
}) {
  const t = useT();
  const ov = t.reports.overview;
  const so = t.reports.salesOverview;

  const { data: comparison, isLoading, error } = usePeriodComparison(range, basis);
  const { data: sales } = useDailySales(range);
  const { data: peak } = usePeakHours(range);

  const cur = comparison?.currency ?? sales?.currency ?? 'USD';
  const current = comparison?.current;
  const previous = comparison?.previous;
  const daily = sales?.daily ?? [];
  const hasSales = !!current && (current.orderCount > 0 || current.grossSalesCents > 0);

  if (isLoading) {
    return (
      <Block className="text-align-center">
        <Preloader />
      </Block>
    );
  }
  if (error) {
    return (
      <EmptyState
        icon="exclamationmark_triangle"
        iconColor="#ff9500"
        title={ov.couldntLoad}
        description={(error as Error).message}
      />
    );
  }
  if (!current || !previous || !hasSales) {
    return <EmptyState icon="chart_bar" title={ov.noSales} description={ov.noSalesHint} />;
  }

  return (
    <>
      {/* Every tile carries the figure, the % change, the baseline it changed
          from, and its own trend line. 4 tiles online-only, 5 with dine-in. */}
      <div className={dineInEnabled ? 'rpt-kpis rpt-kpis-6' : 'rpt-kpis'}>
        <MetricCard
          label={ov.netSales}
          value={formatMoney(current.netSalesCents, cur)}
          delta={pctChange(current.netSalesCents, previous.netSalesCents)}
          previous={ov.vsPrevious(formatMoney(previous.netSalesCents, cur))}
          trend={daily.map((d) => d.netSalesCents)}
        />
        {/* Net = gross − discounts − refunds. Showing net alongside discounts
            and refunds makes the difference legible; a gross tile would just be
            the same number again whenever both are zero. */}
        <MetricCard
          label={ov.refunds}
          value={formatMoney(current.refundsCents, cur)}
          delta={pctChange(current.refundsCents, previous.refundsCents)}
          previous={ov.vsPrevious(formatMoney(previous.refundsCents, cur))}
          trend={daily.map((d) => d.refundsCents)}
          neutral
        />
        {/* orderCount is online-only and sessionCount is dine-in-only (sales-math
            builds them per channel), so they stay two tiles rather than one
            misleading "orders" total. */}
        <MetricCard
          label={ov.onlineOrders}
          value={String(current.orderCount)}
          delta={pctChange(current.orderCount, previous.orderCount)}
          previous={ov.vsPrevious(String(previous.orderCount))}
          trend={daily.map((d) => d.orderCount)}
        />
        {dineInEnabled && (
          <MetricCard
            label={ov.dineInTabs}
            value={String(current.sessionCount)}
            delta={pctChange(current.sessionCount, previous.sessionCount)}
            previous={ov.vsPrevious(String(previous.sessionCount))}
            trend={daily.map((d) => d.sessionCount)}
          />
        )}
        {/* Tips are only ever written by the dine-in take-payment flow
            (table-ordering); online checkout has no tip field at all, so this
            tile would be a permanent 0.00 for an online-only restaurant. */}
        {dineInEnabled && (
          <MetricCard
            label={ov.tips}
            value={formatMoney(current.tipsCents, cur)}
            delta={pctChange(current.tipsCents, previous.tipsCents)}
            previous={ov.vsPrevious(formatMoney(previous.tipsCents, cur))}
            trend={daily.map((d) => d.tipsCents)}
          />
        )}
        <MetricCard
          label={ov.discounts}
          value={formatMoney(current.discountsCents, cur)}
          delta={pctChange(current.discountsCents, previous.discountsCents)}
          previous={ov.vsPrevious(formatMoney(previous.discountsCents, cur))}
          trend={daily.map((d) => d.discountsCents)}
          neutral
        />
      </div>

      <div className="rpt-row">
        <ReportCard className="rpt-main" title={so.revenueTrend}>
          {daily.length > 1 ? (
            <TrendChart
              points={daily.map((d) => ({
                label: formatRangeLabel({ from: d.date, to: d.date }, locale),
                value: d.netSalesCents,
              }))}
              formatValue={(v) => formatMoney(v, cur)}
            />
          ) : (
            <div style={{ color: '#8e8e93', fontSize: 13 }}>{ov.trendNeedsMoreDays}</div>
          )}
        </ReportCard>

        {/* With dine-in off there is only one channel, so the split says nothing
            - the day-part breakdown is the useful companion instead. */}
        {dineInEnabled && sales ? (
          <ReportCard title={so.byChannel}>
            {(() => {
              const max = Math.max(sales.online.netSalesCents, sales.dineIn.netSalesCents, 1);
              return (
                <>
                  <BarRow
                    label={so.online}
                    value={sales.online.netSalesCents}
                    max={max}
                    valueText={formatMoney(sales.online.netSalesCents, cur)}
                  />
                  <BarRow
                    label={so.dineIn}
                    value={sales.dineIn.netSalesCents}
                    max={max}
                    valueText={formatMoney(sales.dineIn.netSalesCents, cur)}
                  />
                </>
              );
            })()}
          </ReportCard>
        ) : (
          peak &&
          peak.dayParts.length > 0 && (
            <ReportCard title={so.byTimeOfDay}>
              <DayPartBars parts={peak.dayParts} cur={cur} />
            </ReportCard>
          )
        )}
      </div>

      {/* Dine-in off: the hour profile has no Operations segment to live in, and
          "when do orders come in" still matters for online-only. */}
      {!dineInEnabled && peak && peak.hours.length > 0 && (
        <div className="rpt-row">
          <ReportCard className="rpt-wide" title={ov.byHour}>
            <HourBars hours={peak.hours} cur={cur} />
          </ReportCard>
        </div>
      )}
    </>
  );
}

/** Day-part revenue bars; shared with the Operations section. */
export function DayPartBars({
  parts,
  cur,
}: {
  parts: { dayPart: keyof typeof DAY_PART_KEY; netSalesCents: number }[];
  cur: string;
}) {
  const t = useT();
  const max = Math.max(...parts.map((d) => d.netSalesCents), 1);
  return (
    <>
      {parts.map((d) => (
        <BarRow
          key={d.dayPart}
          label={t.reports.dayParts[DAY_PART_KEY[d.dayPart]]}
          value={d.netSalesCents}
          max={max}
          valueText={formatMoney(d.netSalesCents, cur)}
        />
      ))}
    </>
  );
}

/** Hour-of-day columns, peak hour picked out. */
export function HourBars({
  hours,
  cur,
}: {
  hours: { hour: number; netSalesCents: number }[];
  cur: string;
}) {
  const peakIndex = hours.reduce(
    (best, h, i) => (h.netSalesCents > hours[best].netSalesCents ? i : best),
    0,
  );
  return (
    <ColumnChart
      points={hours.map((h) => ({
        label: `${String(h.hour).padStart(2, '0')}:00`,
        value: h.netSalesCents,
      }))}
      highlightIndex={peakIndex}
      formatValue={(v) => formatMoney(v, cur)}
    />
  );
}
